import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckIcon } from "lucide-react";
import { Group } from "@/lib/api/group";
import { ReloadIcon } from "@radix-ui/react-icons";
import { useCurrentUser, UserRole } from "@/lib/api/user";

interface EditGroupDialogProps {
  group: Group | null;
  onClose: () => void;
  onSubmit: (
    name: string,
    is_billable_group?: boolean,
    care_reviews_enabled?: boolean,
  ) => void;
  isLoading: boolean;
}

export function EditGroupDialog({ group, onClose, onSubmit, isLoading }: EditGroupDialogProps) {
  const { data: currentUser } = useCurrentUser();
  const isSuperAdmin = currentUser?.role === UserRole.SUPERADMIN;
  const [name, setName] = useState("");
  const [isBillableGroup, setIsBillableGroup] = useState(false);
  const [careReviewsEnabled, setCareReviewsEnabled] = useState(false);

  useEffect(() => {
    if (group) {
      setName(group.name);
      setIsBillableGroup(group.is_billable_group ?? false);
      setCareReviewsEnabled(group.care_reviews_enabled ?? false);
    }
  }, [group]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(
      name,
      isBillableGroup,
      isSuperAdmin ? careReviewsEnabled : undefined,
    );
  };

  return (
    <Dialog open={!!group} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Group</DialogTitle>
          <DialogDescription>Update the group name</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter group name"
            required
          />
          {isSuperAdmin && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="billable-group"
                checked={isBillableGroup}
                onCheckedChange={(checked) => setIsBillableGroup(checked === true)}
              />
              <label
                htmlFor="billable-group"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Billable Group
              </label>
            </div>
          )}
          {isSuperAdmin && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="care-reviews"
                checked={careReviewsEnabled}
                onCheckedChange={(checked) =>
                  setCareReviewsEnabled(checked === true)
                }
              />
              <label
                htmlFor="care-reviews"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Care reviews
              </label>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <ReloadIcon className="mr-2 size-4 animate-spin" aria-hidden="true" />
              ) : (
                <CheckIcon className="mr-2 size-4" aria-hidden="true" />
              )}
              Update Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}